/**
 * ManifestSkillEffect — Conjuration Bind path apotheosis active.
 *
 * The familiar reaches full autonomous expression. 150 HP, 22 damage per
 * attack. Manifest-tier AI: tactical foundation plus grudge list targeting
 * (prioritizes enemies that have dealt the most damage to the player),
 * 20% HP retreat threshold, 1.5× player aggro generation, 600ms intercept
 * cooldown, and adapted abilities at 70% of the player's power.
 *
 * The grudge list is maintained by the integration layer — the
 * CombatHitResolver emits player-damage events with sourceEnemyId, and the
 * Manifest AI module accumulates them into a Map<enemyId, damageDealt>.
 * The list persists for the familiar's lifetime and clears on death.
 *
 * Upgrade hooks (resolved via 'bind' scope, inherited from the path):
 *   - Fortify: hpBonus additive (+25)
 *   - Resonant: damageBonus additive (+5)
 *   - Intercept: always enabled at Manifest tier (600ms cooldown intrinsic)
 *
 * Single adapted behavior from the player's highest non-Conjuration tree,
 * scaled to 70% power via adaptedAbilityPower on the spawn config.
 *
 * Very long cooldown tier (4000ms). Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Manifest
 */

import type { SkillEffect } from '../../SkillEffect.js';
import {
  type CompanionSummonConfig,
  type AdaptedBehaviorConfig,
  resolveAdaptedBehavior,
} from './BindSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface ManifestDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
    getMinEffect(skillId: string, key: string): number | undefined;
    getMaxEffect(skillId: string, key: string): number | undefined;
  };
  readonly player: {
    getPosition(): { x: number; y: number };
  };
  readonly targeting: {
    /** Returns true if the familiar should fail to manifest (XP-based freshness check). */
    shouldFizzle(): boolean;
  };
  readonly summons: {
    spawn(config: CompanionSummonConfig, x: number, y: number): string;
  };
  readonly progression: {
    /** Returns the tree ID with the most XP among all non-Conjuration trees, or null if none. */
    getHighestNonConjurationTree(): string | null;
  };
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface ManifestSkillConfig {
  readonly cooldownMs: number;
  /** Base companion HP before upgrade bonuses. */
  readonly baseHp: number;
  /** Base companion damage per hit before upgrade bonuses. */
  readonly baseDamage: number;
  /** Base companion movement speed. */
  readonly baseSpeed: number;
  /** Max distance from player before returning (tiles). */
  readonly leashRadiusTiles: number;
  /** Max distance from familiar to target before engaging (tiles). */
  readonly attackRangeTiles: number;
  /** Delay between swings in ms. */
  readonly attackRecoveryMs: number;
  /** Aggro weight — 1.5× player aggro at Manifest tier. */
  readonly baseAggroWeight: number;
  /** HP fraction (0–1) below which the familiar retreats to follow mode. */
  readonly retreatHpThreshold: number;
  /** Intercept cooldown in ms — intrinsic 600ms at Manifest tier. */
  readonly interceptCooldownMs: number;
  /** Scaling factor for adapted ability power (0–1). */
  readonly adaptedAbilityPower: number;
}

const DEFAULT_CONFIG: ManifestSkillConfig = {
  cooldownMs: 4000,              // very_long cooldown tier
  baseHp: 150,                   // spec: 150 HP
  baseDamage: 22,                // spec: 22 damage per hit
  baseSpeed: 1.0,                // integration layer applies px/s
  leashRadiusTiles: 2,           // spec: stay within 2 tiles of player
  attackRangeTiles: 3,           // spec: attack enemies within 3 tiles
  attackRecoveryMs: 1000,        // spec: 1s recovery between swings
  baseAggroWeight: 1.5,          // spec: 1.5× player aggro
  retreatHpThreshold: 0.2,       // spec: retreat at 20% HP
  interceptCooldownMs: 600,      // spec: 600ms (half of Anchored's 1200ms)
  adaptedAbilityPower: 0.7,      // spec: 70% of player's power
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class ManifestSkillEffect implements SkillEffect {
  readonly skillId = 'manifest';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'FIZZLE';

  private readonly deps: ManifestDeps;
  private readonly config: ManifestSkillConfig;

  private _cooldownRemaining = 0;
  private _familiarAlive = false;
  private _familiarId = '';
  private _fizzled = false;
  private _spawnsCount = 0;
  private _resolvedHp = 0;
  private _resolvedDamage = 0;
  private _adaptedBehavior: AdaptedBehaviorConfig = { type: 'none' };

  constructor(deps: ManifestDeps, config: Partial<ManifestSkillConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters (observable by scenarios and integration layer) ---

  get cooldown(): number { return this._cooldownRemaining; }
  get familiarAlive(): boolean { return this._familiarAlive; }
  get familiarId(): string { return this._familiarId; }
  get fizzled(): boolean { return this._fizzled; }
  get spawnsCount(): number { return this._spawnsCount; }
  get resolvedHp(): number { return this._resolvedHp; }
  get resolvedDamage(): number { return this._resolvedDamage; }
  get adaptedBehavior(): AdaptedBehaviorConfig { return this._adaptedBehavior; }

  // --- Integration layer hooks ---

  /**
   * Called by the integration layer when the companion dies.
   * Wired to SummonSystem's 'died' event for archetype 'companion'.
   * Grudge list clears on death — new familiar starts with no grudges.
   */
  onFamiliarDied(): void {
    this._familiarAlive = false;
    this._familiarId = '';
  }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0 && !this._familiarAlive;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Freshness fizzle check — consumes cooldown but does not spawn
    this._fizzled = this.deps.targeting.shouldFizzle();
    if (this._fizzled) {
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    const pos = this.deps.player.getPosition();

    // Resolve HP: base + bind-path hpBonus (Fortify)
    const bindHpBonus = this.deps.resolver.getAdditiveEffect('bind', 'hpBonus');
    this._resolvedHp = this.config.baseHp + bindHpBonus;

    // Resolve damage: base + bind-path damageBonus (Resonant)
    const bindDmgBonus = this.deps.resolver.getAdditiveEffect('bind', 'damageBonus');
    this._resolvedDamage = this.config.baseDamage + bindDmgBonus;

    // Intercept min HP from resolver (Fortify contributes via max-aggregation)
    const interceptMinHp = this.deps.resolver.getMaxEffect('bind', 'interceptMinHp') ?? 15;

    // Single adapted behavior from highest non-Conjuration tree
    const highestTree = this.deps.progression.getHighestNonConjurationTree();
    this._adaptedBehavior = resolveAdaptedBehavior(highestTree);

    const companionConfig: CompanionSummonConfig = {
      id: 'manifest_companion',
      archetype: 'companion',
      baseHp: this._resolvedHp,
      baseDamage: this._resolvedDamage,
      baseSpeed: this.config.baseSpeed,
      leashRadiusTiles: this.config.leashRadiusTiles,
      aggroWeight: this.config.baseAggroWeight,
      aiMode: 'manifest',
      attackRangeTiles: this.config.attackRangeTiles,
      attackRecoveryMs: this.config.attackRecoveryMs,
      stunTargetPriority: true,
      retreatHpThreshold: this.config.retreatHpThreshold,
      threatPositioning: true,
      interceptEnabled: true,
      interceptCooldownMs: this.config.interceptCooldownMs,
      interceptMinHp,
      adaptedBehavior: this._adaptedBehavior,
      adaptedAbilityPower: this.config.adaptedAbilityPower,
    };

    this._familiarId = this.deps.summons.spawn(companionConfig, pos.x, pos.y);
    this._familiarAlive = true;
    this._spawnsCount++;

    this._cooldownRemaining = this.config.cooldownMs;
    return true;
  }

  deactivate(): void {
    // Instant skill — no toggle state
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
    this._familiarAlive = false;
    this._familiarId = '';
  }
}
